import type { LambdaMethod } from '../../../../@types/samples/types';

export type LambdaMethodInfo = {
  value: LambdaMethod;
  label: string;
  summary: string;
  pros: string[];
  cons: string[];
  /** Badged in the UI as the best-fitting method (see validation ranking). */
  recommended?: boolean;
};

/**
 * Brooks-Corey / Lambda solver methods, ordered as shown in the selector.
 *
 * Ranking (mean RMSE of Pc = Pe·Swn^lambda vs the laboratory points across the
 * 21 reference samples in sheets 62-82 of the SCAL refinement workbook):
 *   1. nlsq          (7.23)   <- lowest mean error, "recommended"
 *   2. weighted_nlsq (11.20)  (optimizes relative error, so its absolute RMSE ranks lower)
 *   3. mean_slope    (12.03)
 *   4. ols_loglog    (12.78)
 * The default remains "mean_slope" for backward compatibility with previously
 * saved samples; the badge points users toward the best-fitting solver.
 *
 * A "free-Swir" nonlinear variant was prototyped and validated (best mean RMSE)
 * but removed: it fits Swir as a third parameter, so its Pe/lambda are tuned to
 * a Swir that diverges from the lab Swir the correlation / Kr×Pc model uses,
 * making it unusable in the correlation. See task-history SOLVER.md.
 */
export const LAMBDA_METHODS: LambdaMethodInfo[] = [
  {
    value: 'mean_slope',
    label: 'Mean slope',
    summary:
      'Entry pressure Pe from an OLS log-log intercept; lambda is the average of the per-point anchored log-log slopes.',
    pros: [
      'Simple, no iterative fitting.',
      'Uses the same Swir basis as the other standard methods.'
    ],
    cons: [
      'Unstable: points near Swn = 1 (small ln Swn) blow up and dominate the average.',
      'Collapses toward a single point when few laboratory rows fall inside 0 < Swn < 1.',
      'Highest curve-fit error of the available methods.'
    ]
  },
  {
    value: 'ols_loglog',
    label: 'OLS log-log',
    summary:
      'A single ordinary-least-squares regression of ln(Pc) on ln(Swn) over all valid interior points; lambda is the fitted slope and Pe = exp(intercept).',
    pros: [
      'Uses every valid laboratory point jointly — no single point dominates.',
      'Stable and deterministic; Pe and lambda come from one consistent fit.',
      'Preserves the Brooks-Corey log-log methodology.'
    ],
    cons: [
      'Minimizes error in log space, so it can under-weight the high-Pc points.',
      'Still sensitive to how points spread in ln(Swn).'
    ]
  },
  {
    value: 'nlsq',
    label: 'Nonlinear least-squares',
    summary:
      'Bounded nonlinear least-squares fit of Pc = Pe·Swn^lambda directly in pressure-saturation space, seeded from the OLS solution.',
    pros: [
      'Minimizes the actual Pc residuals — best visual and numeric fit to the lab points.',
      'Lowest and most consistent curve-fit error across the reference samples.',
      'Keeps the lab Swir, so it is safe to use in the correlation.',
      'Robust: bounded and seeded, falls back to OLS if it cannot converge.'
    ],
    cons: ['Iterative (still fast for two parameters).'],
    recommended: true
  },
  {
    value: 'weighted_nlsq',
    label: 'Weighted nonlinear least-squares',
    summary:
      'Like nonlinear least-squares, but minimizes the relative error (each residual is divided by Pc), so low- and high-pressure points count evenly.',
    pros: [
      'Balances the whole pressure range instead of chasing the high-Pc tail.',
      'Best choice when the data spans several orders of magnitude in Pc.',
      'Robust: bounded, seeded from OLS, falls back to OLS.'
    ],
    cons: [
      'Optimizes relative (not absolute) error, so its absolute RMSE can look worse than plain nlsq.',
      'Iterative.'
    ]
  }
];

export const LAMBDA_METHOD_LABELS: Record<LambdaMethod, string> = {
  mean_slope: 'Mean slope',
  ols_loglog: 'OLS log-log',
  nlsq: 'Nonlinear least-squares',
  weighted_nlsq: 'Weighted nonlinear least-squares'
};
